import type { MinionKind } from '@/entities/minion-defs';
import type { ThemeId } from './themes';

/**
 * Sector campaign content (docs/01 run structure, docs/02 sector map). A run
 * is ONE sector: `rooms` regular rooms (waves + a boss from the sector's
 * pool), then the FINALE room (index `rooms`). Clearing the finale wins the
 * sector and unlocks the next one. Difficulty multipliers per sector live in
 * config/difficulty.ts (same index).
 */

/** Waves + light kinds of one room (index = room in sector; last = finale). */
export interface SectorRoomPlan {
  /** Waves before the boss: lo..hi, picked by the seeded room hash. The
   *  campaign uses fixed counts [n, n] — durations are calibrated on them
   *  (docs/01 duration calibration, polish round) */
  readonly waves: readonly [number, number];
  /** Light squad kinds this room may field (empty → no waves) */
  readonly kinds: ReadonlyArray<MinionKind>;
}

/** Finale modifier: extra levels (→ evolve tiers) + HP on top of the sector. */
export interface FinaleMod {
  readonly levels: number;
  readonly hpMul: number;
}

export interface SectorDef {
  readonly id: string;
  /** i18n: `sector.<id>.name` / `.tag` */
  readonly nameKey: string;
  readonly tagKey: string;
  /** Card accent on the sector select screen */
  readonly accent: number;
  /** Map theme of every room (config/themes.ts, docs/04) */
  readonly theme: ThemeId;
  /** Regular rooms; the finale is room index `rooms` */
  readonly rooms: number;
  /** Regular boss pool — the shuffle-bag shows each once (≥ rooms) */
  readonly bosses: ReadonlyArray<string>;
  readonly finale: string;
  readonly finaleMod: FinaleMod;
  /**
   * Layout id per room (rooms + 1 entries, finale last). A route id makes
   * the room an ADVANCE room (segments + gates + chamber); any other id is a
   * classic ARENA room (docs/01 room kinds). Room 1 stays an arena.
   */
  readonly layouts: ReadonlyArray<string>;
  /** Per-room waves + enemy introduction (rooms + 1 entries) */
  readonly plan: ReadonlyArray<SectorRoomPlan>;
  /** Room-in-sector gates for bunkers / the dome / mid-fight side squads */
  readonly gates: { readonly bunker: number; readonly dome: number; readonly adds: number };
}

// Enemy introduction ladder (D5): S1 teaches one or two kinds per room
const K1: ReadonlyArray<MinionKind> = ['dasher'];
const K2: ReadonlyArray<MinionKind> = [...K1, 'fly'];
const K3: ReadonlyArray<MinionKind> = [...K2, 'weaver'];
const K4: ReadonlyArray<MinionKind> = [...K3, 'mite', 'drone'];
const K5: ReadonlyArray<MinionKind> = [...K4, 'splitter'];
const K6: ReadonlyArray<MinionKind> = [...K5, 'sniper'];
/** Every light kind (the elite share comes from difficulty.eliteRatio) */
const ALL: ReadonlyArray<MinionKind> = [...K6, 'elite'];

export const SECTORS: ReadonlyArray<SectorDef> = [
  {
    // S1 — intro + mechanical: the tutorial roster, Warden closes it
    id: 's1',
    nameKey: 'sector.s1.name',
    tagKey: 'sector.s1.tag',
    accent: 0x45d9c5,
    theme: 'scrap',
    rooms: 6,
    bosses: ['sentinel', 'carrier', 'hive', 'quartet', 'prism', 'magnet'],
    finale: 'warden',
    finaleMod: { levels: 1, hpMul: 1 },
    // ARENA · ADVANCE · ARENA · ARENA · ADVANCE · ARENA · FINALE (big field)
    layouts: [
      'void-open', 'scrap-spine', 'nebula-pillars', 'scrap-foundry',
      'scrap-dogleg', 'forest-grove', 'scrap-yard',
    ],
    plan: [
      { waves: [3, 3], kinds: K1 },
      { waves: [3, 3], kinds: K2 },
      { waves: [3, 3], kinds: K3 },
      { waves: [3, 3], kinds: K4 },
      { waves: [3, 3], kinds: K5 },
      { waves: [3, 3], kinds: K6 },
      { waves: [3, 3], kinds: ALL },
    ],
    gates: { bunker: 3, dome: 5, adds: 3 },
  },
  {
    // S2 — the Elementals (+ the gravity well), Avatar closes it
    id: 's2',
    nameKey: 'sector.s2.name',
    tagKey: 'sector.s2.tag',
    accent: 0xff7a30,
    theme: 'core',
    rooms: 6,
    bosses: ['pyre', 'tide', 'terra', 'gale', 'null', 'singularity'],
    finale: 'avatar',
    finaleMod: { levels: 1, hpMul: 1 },
    // ARENA · ADVANCE · ARENA · ADVANCE · ARENA · ADVANCE · FINALE (big field)
    layouts: [
      'magma-cauldron', 'core-conduit', 'forest-chasm', 'core-switchback',
      'magma-crossfire', 'core-crucible', 'core-forge',
    ],
    plan: [
      { waves: [4, 4], kinds: K4 },
      { waves: [3, 3], kinds: K5 },
      { waves: [3, 3], kinds: K6 },
      { waves: [3, 3], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
    ],
    gates: { bunker: 1, dome: 4, adds: 2 },
  },
  {
    // S3 — the brood: chains, tentacles and blobs; the Broodworm Queen
    // closes it with a finale modifier (Hive returns as a veteran)
    id: 's3',
    nameKey: 'sector.s3.name',
    tagKey: 'sector.s3.tag',
    accent: 0x7ec850,
    theme: 'brood',
    rooms: 6,
    bosses: ['serpent', 'kraken', 'centipede', 'splitter', 'webspinner', 'hive'],
    finale: 'broodworm',
    finaleMod: { levels: 3, hpMul: 1.35 },
    // ARENA · ADVANCE · ARENA · ADVANCE · ARENA · ADVANCE · FINALE (big field)
    layouts: [
      'abyss-trench', 'brood-tunnel', 'abyss-vortex', 'brood-gullet',
      'nebula-expanse', 'brood-warren', 'brood-nest',
    ],
    plan: [
      { waves: [3, 3], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
    ],
    gates: { bunker: 0, dome: 3, adds: 1 },
  },
  {
    // S4 — anomalies and the last armada; Chronos closes the campaign with
    // a finale modifier (Singularity + Prism return as veterans)
    id: 's4',
    nameKey: 'sector.s4.name',
    tagKey: 'sector.s4.tag',
    accent: 0xb07cff,
    theme: 'rim',
    rooms: 6,
    bosses: ['mirror', 'admiral', 'bastion', 'stalker', 'singularity', 'prism'],
    finale: 'chronos',
    finaleMod: { levels: 3, hpMul: 1.4 },
    // ARENA · ADVANCE · ARENA · ADVANCE · ARENA · ADVANCE · FINALE (big field)
    layouts: [
      'void-fracture', 'rim-causeway', 'rim-shardfield', 'rim-spiral',
      'void-ring', 'rim-fracture', 'rim-observatory',
    ],
    // Longest sector: 13.5-14.5 min target, 15 min cap (docs/01)
    plan: [
      { waves: [3, 3], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
      { waves: [4, 4], kinds: ALL },
      { waves: [3, 3], kinds: ALL },
    ],
    gates: { bunker: 0, dome: 2, adds: 0 },
  },
];

/** Sector by index, clamped into the table. */
export function sectorAt(index: number): SectorDef {
  return SECTORS[Math.max(0, Math.min(SECTORS.length - 1, index))]!;
}
